import { HeadingTagType } from '@lexical/rich-text';
import { LexicalEditor } from 'lexical';
import React from 'react';
import { useEditorState } from '../../../BaseEditor/hooks/useBaseEditorFormatState';
import DropDown, { DropDownItem } from '../../../ui/DropDown';
import { dropDownActiveClass } from './FrontDropdown';

export const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  'commentable-paragraph': 'Normal',
  quote: 'Quote'
};

interface BlockFormatDropDownProps {
  editor: LexicalEditor;
  blockType: keyof typeof blockTypeToBlockName;
  disabled?: boolean;
}

export function BlockFormatDropDown({
  editor,
  blockType,
  disabled = false
}: BlockFormatDropDownProps): React.JSX.Element {
  const { formatParagraph, formatHeading, formatList, formatQuote, formatCode } = useEditorState(
    editor as any
  );

  return (
    <DropDown
      disabled={disabled}
      buttonClassName="toolbar-item block-controls"
      buttonIconClassName={'icon block-type ' + blockType}
      buttonLabel={blockTypeToBlockName[blockType]}
      buttonAriaLabel="Formatting options for text style"
    >
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'paragraph')}
        onClick={formatParagraph}
      >
        <i className="icon paragraph" />
        <span className="text">Normal</span>
      </DropDownItem>
      {(['h1', 'h2', 'h3'] as const).map((tag) => (
        <DropDownItem
          key={tag}
          className={'item ' + dropDownActiveClass(blockType === tag)}
          onClick={() => formatHeading(tag as HeadingTagType)}
        >
          <i className={`icon ${tag}`} />
          <span className="text">Heading {tag.slice(1)}</span>
        </DropDownItem>
      ))}
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'bullet')}
        onClick={() => formatList('bullet')}
      >
        <i className="icon bullet-list" />
        <span className="text">Bullet List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'number')}
        onClick={() => formatList('number')}
      >
        <i className="icon numbered-list" />
        <span className="text">Numbered List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'check')}
        onClick={() => formatList('check')}
      >
        <i className="icon check-list" />
        <span className="text">Check List</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'quote')}
        onClick={formatQuote}
      >
        <i className="icon quote" />
        <span className="text">Quote</span>
      </DropDownItem>
      <DropDownItem
        className={'item ' + dropDownActiveClass(blockType === 'code')}
        onClick={formatCode}
      >
        <i className="icon code" />
        <span className="text">Code Block</span>
      </DropDownItem>
    </DropDown>
  );
}
